import type { MutationProposal } from "~/helpers/aiChat/chatStorage";
import type { ProposalSummary } from "~/helpers/aiChat/summarizeProposal";
import type { PriceImportApply, PriceImportCard, PriceImportCardRow } from "~/services/aiOrchestrator";

import { Box, ButtonBase, CircularProgress, Collapse, Icon, Link, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { CancelButton, SubmitButton } from "~/components/Buttons";
import FernieCardHeader from "~/components/FernieCardHeader";
import FernieCardShell from "~/components/FernieCardShell";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import translatePriceImport, {
	formatAppliedSubtotal,
	formatCost,
	formatLabeledValue,
	formatPriceChange,
	formatRoyalty,
	isApplySuccess
} from "~/helpers/aiChat/priceImportCardText";
import buildProposalNameResolver from "~/helpers/aiChat/proposalNameCache";
import summarizeProposal from "~/helpers/aiChat/summarizeProposal";
import summarizeToolResult from "~/helpers/aiChat/summarizeToolResult";
import { stringFormatDateTime } from "~/helpers/timestampFormat";
import { useAppSelector } from "~/hooks/reduxHooks";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { MATERIAL_ITEMS_PATH } from "~produce/production/supplierPriceImports/constants";

interface FernieConfirmCardProps {
	busy: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	proposal: MutationProposal;
}

interface PendingCardProps extends FernieConfirmCardProps {
	summary: ProposalSummary;
}

const readableResultText = (raw: string | undefined): string | undefined =>
	summarizeToolResult(raw, { commit: true }).summary;

const ProposalDetails = ({ summary }: { summary: ProposalSummary }) => {
	if (summary.fields.length === 0 && summary.items.length === 0) return null;
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1.5, pb: 1 }}>
			{summary.fields.map((field) => (
				<Box key={field.label} sx={{ display: "flex", gap: 1 }}>
					<CaptionText sx={{ color: "text.secondary", flex: "0 0 92px", fontWeight: 600 }}>{field.label}</CaptionText>
					<BodyTextSmall sx={{ color: "text.primary", fontWeight: 500 }}>{field.value}</BodyTextSmall>
				</Box>
			))}
			{summary.items.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: summary.fields.length > 0 ? 0.5 : 0 }}>
					<CaptionText sx={{ color: "text.secondary", fontWeight: 600 }}>Items</CaptionText>
					{summary.items.map((item, index) => (
						<BodyTextSmall key={`${item.name}-${index}`} sx={{ color: "text.primary" }}>
							{item.quantity ? `${item.quantity} × ${item.name}` : item.name}
						</BodyTextSmall>
					))}
				</Box>
			)}
		</Box>
	);
};

const RawArgsExpander = ({ rawArgs }: { rawArgs: string }) => {
	const [open, setOpen] = useState(false);
	if (!rawArgs) return null;
	return (
		<Box sx={{ borderTop: "1px solid", borderTopColor: "divider", px: 1.5, py: 1 }}>
			<ButtonBase
				onClick={() => setOpen((prev) => !prev)}
				sx={{
					...fernieButtonSx.focusRing,
					"&:hover": { color: "text.primary" },
					alignItems: "center",
					color: "text.secondary",
					cursor: "pointer",
					display: "inline-flex",
					gap: 0.5
				}}
			>
				<Icon sx={{ fontSize: 16 }}>{open ? "expand_less" : "expand_more"}</Icon>
				<CaptionText sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
					{open ? "Hide raw arguments" : "Show raw arguments"}
				</CaptionText>
			</ButtonBase>
			<Collapse in={open} unmountOnExit>
				<Box
					component="pre"
					sx={{
						backgroundColor: "action.hover",
						borderRadius: "8px",
						color: "text.secondary",
						fontFamily: "'JetBrains Mono', monospace",
						fontSize: "0.68rem",
						lineHeight: 1.5,
						m: 0,
						maxHeight: 220,
						mt: 1,
						overflow: "auto",
						p: 1.25,
						whiteSpace: "pre-wrap",
						wordBreak: "break-word"
					}}
				>
					{rawArgs}
				</Box>
			</Collapse>
		</Box>
	);
};

const PriceImportRow = ({ row }: { row: PriceImportCardRow }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
		<BodyTextSmall sx={{ color: "text.primary", fontWeight: 600 }}>
			{row.description} ({row.itemCode})
		</BodyTextSmall>
		<CaptionText sx={{ color: "text.secondary" }}>
			{translatePriceImport(row.disposition === "CREATE_MATERIAL" ? "newMaterial" : "existingMaterial")}
		</CaptionText>
		<CaptionText>
			{formatLabeledValue(
				"base",
				formatPriceChange(row.today?.basePerEach, row.basePerEach ?? translatePriceImport("unknownAmount"))
			)}
		</CaptionText>
		<CaptionText>
			{formatLabeledValue(
				"royalty",
				formatPriceChange(row.today ? formatRoyalty(row.today) : undefined, formatRoyalty(row))
			)}
		</CaptionText>
		<CaptionText>
			{formatLabeledValue("cost", formatPriceChange(row.today ? formatCost(row.today) : undefined, formatCost(row)))}
		</CaptionText>
		{row.disposition !== "CREATE_MATERIAL" && !row.today && row.comparison && (
			<CaptionText sx={{ color: "text.secondary" }}>{row.comparison}</CaptionText>
		)}
		{row.note && <CaptionText sx={{ color: "warning.main", fontWeight: 600 }}>{row.note}</CaptionText>}
	</Box>
);

const PriceImportDetails = ({ card }: { card: PriceImportCard }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1.5, pb: 1 }}>
		<BodyTextSmall sx={{ color: "text.primary", fontWeight: 600 }}>
			{translatePriceImport("header", {
				file: card.fileName,
				revision: String(card.reviewRevision),
				vendor: card.vendorName
			})}
		</BodyTextSmall>
		{card.summaryLines.map((line) => (
			<BodyTextSmall key={line}>{line}</BodyTextSmall>
		))}
		{card.rows.map((row) => (
			<PriceImportRow key={row.sourceRowId} row={row} />
		))}
		<CaptionText sx={{ color: "text.secondary" }}>{card.todayNote}</CaptionText>
		<Link href={card.reviewLink} rel="noopener" target="_blank" underline="always" variant="caption">
			{translatePriceImport("openReview")}
		</Link>
		<CaptionText sx={{ color: "text.secondary" }}>
			{translatePriceImport("expires", { when: stringFormatDateTime(card.expiresAt) })}
		</CaptionText>
	</Box>
);

const ImportResultsLink = ({ href }: { href: string }) => (
	<Link href={href} rel="noopener" target="_blank" underline="always" variant="caption">
		{translatePriceImport("openResults")}
	</Link>
);

const PriceImportApplyDetails = ({ apply }: { apply: PriceImportApply }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1.5, pb: 1 }}>
		<CaptionText sx={{ color: "text.secondary" }}>
			{translatePriceImport("counts", { notSaved: String(apply.rowsNotSavedYet), waiting: String(apply.rowsWaiting) })}
		</CaptionText>
		{apply.rows.map((row) => {
			const subtotal = formatAppliedSubtotal(row);
			return (
				<Box key={row.sourceRowId}>
					<BodyTextSmall>
						{row.materialItemId ? (
							<Link href={`${MATERIAL_ITEMS_PATH}/${row.materialItemId}`} rel="noopener" target="_blank">
								{row.label}
							</Link>
						) : (
							row.label
						)}
					</BodyTextSmall>
					{subtotal && <CaptionText sx={{ color: "text.secondary" }}>{subtotal}</CaptionText>}
				</Box>
			);
		})}
		<ImportResultsLink href={apply.resultsLink} />
	</Box>
);

const PendingCard = ({ busy, onCancel, onConfirm, proposal, summary }: PendingCardProps) => (
	<FernieCardShell accent="primary">
		<FernieCardHeader color="primary.main" icon="bolt" mono={false} toolName={summary.title} />
		<CaptionText
			sx={{
				color: "primary.main",
				fontSize: "0.62rem",
				fontWeight: 700,
				letterSpacing: "0.07em",
				px: 1.5,
				pt: 0.5,
				textTransform: "uppercase"
			}}
		>
			Awaiting your confirmation
		</CaptionText>
		{proposal.priceImport ? (
			<PriceImportDetails card={proposal.priceImport} />
		) : (
			<>
				<ProposalDetails summary={summary} />
				<RawArgsExpander rawArgs={summary.rawArgs} />
			</>
		)}
		{proposal.cancelNote && (
			<CaptionText sx={{ color: "error.main", fontWeight: 500, px: 1.5, pb: 1 }}>{proposal.cancelNote}</CaptionText>
		)}
		<Box
			sx={{
				borderTop: "1px solid",
				borderTopColor: "divider",
				display: "flex",
				gap: 1,
				px: 1.5,
				py: 1
			}}
		>
			<SubmitButton
				disabled={busy}
				fullWidth={false}
				onClick={onConfirm}
				size="small"
				startIcon={busy ? undefined : <Icon>check</Icon>}
				sx={{
					...fernieButtonSx.focusRing,
					borderRadius: "8px",
					flex: 1,
					fontSize: "0.78rem",
					minWidth: 0,
					textTransform: "none"
				}}
			>
				{busy && <CircularProgress color="inherit" size={14} />}
				<Box component="span" sx={busy ? fernieButtonSx.visuallyHidden : undefined}>
					Confirm
				</Box>
			</SubmitButton>
			<CancelButton
				disabled={busy}
				onClick={onCancel}
				size="small"
				sx={{
					...fernieButtonSx.focusRing,
					borderRadius: "8px",
					fontSize: "0.78rem",
					textTransform: "none"
				}}
			/>
		</Box>
	</FernieCardShell>
);

const ConfirmedCard = ({ resultSummary, title }: { resultSummary: string; title: string }) => (
	<FernieCardShell accent="success" tinted>
		<FernieCardHeader color="success.main" icon="check_circle" mono={false} toolName={title} />
		<Typography
			sx={{
				color: "text.primary",
				fontSize: "0.82rem",
				fontWeight: 500,
				lineHeight: 1.5,
				px: 1.5,
				py: 1
			}}
		>
			{resultSummary}
		</Typography>
	</FernieCardShell>
);

const FailedCard = ({
	resultsLink,
	resultSummary,
	title
}: {
	resultsLink?: string;
	resultSummary: string;
	title: string;
}) => (
	<FernieCardShell accent="error" tinted>
		<FernieCardHeader color="error.main" icon="error" mono={false} toolName={title} />
		<Typography
			sx={{
				color: "error.main",
				fontSize: "0.82rem",
				fontWeight: 500,
				lineHeight: 1.5,
				px: 1.5,
				py: 1
			}}
		>
			{resultSummary}
		</Typography>
		{resultsLink && (
			<Box sx={{ px: 1.5, pb: 1 }}>
				<ImportResultsLink href={resultsLink} />
			</Box>
		)}
	</FernieCardShell>
);

const CancelledCard = ({ text, title }: { text: string; title: string }) => (
	<FernieCardShell accent="neutral">
		<FernieCardHeader color="text.disabled" icon="cancel" mono={false} toolName={title} />
		<Typography sx={{ color: "text.disabled", fontSize: "0.7rem", px: 1.5, py: 1 }}>{text}</Typography>
	</FernieCardShell>
);

const PriceImportResultCard = ({ proposal, title }: { proposal: MutationProposal; title: string }) => {
	const { priceImportApply: apply } = proposal;
	const isApplyComplete = isApplySuccess(apply);
	const accent = isApplyComplete ? "success" : "error";
	const message =
		(isApplyComplete ? undefined : apply?.message) || readableResultText(proposal.resultSummary) || "Done.";
	return (
		<FernieCardShell accent={accent} tinted>
			<FernieCardHeader
				color={`${accent}.main`}
				icon={isApplyComplete ? "check_circle" : "warning"}
				mono={false}
				toolName={title}
			/>
			<BodyTextSmall
				sx={{
					color: isApplyComplete ? "text.primary" : "error.main",
					fontSize: "0.82rem",
					fontWeight: 500,
					lineHeight: 1.5,
					px: 1.5,
					py: 1
				}}
			>
				{message}
			</BodyTextSmall>
			{apply && <PriceImportApplyDetails apply={apply} />}
		</FernieCardShell>
	);
};

const CommittingCard = ({ proposal, title }: { proposal: MutationProposal; title: string }) => (
	<FernieCardShell accent="primary">
		<FernieCardHeader color="primary.main" icon="hourglass_empty" mono={false} toolName={title} />
		<BodyTextSmall sx={{ color: "text.secondary", fontWeight: 500, px: 1.5, py: 1 }}>
			{proposal.priceImport ? translatePriceImport("committing") : readableResultText(proposal.resultSummary)}
		</BodyTextSmall>
		{proposal.priceImport && (
			<Box sx={{ px: 1.5, pb: 1 }}>
				<ImportResultsLink href={proposal.priceImport.reviewLink} />
			</Box>
		)}
	</FernieCardShell>
);

const OutcomeUnknownCard = ({ card, title }: { card: PriceImportCard; title: string }) => (
	<FernieCardShell accent="error" tinted>
		<FernieCardHeader color="error.main" icon="warning" mono={false} toolName={title} />
		<BodyTextSmall sx={{ color: "error.main", fontWeight: 500, px: 1.5, py: 1 }}>
			{translatePriceImport("outcomeUnknown")}
		</BodyTextSmall>
		<Box sx={{ px: 1.5, pb: 1 }}>
			<ImportResultsLink href={card.reviewLink} />
		</Box>
	</FernieCardShell>
);

const FernieConfirmCard = (props: FernieConfirmCardProps) => {
	const { proposal } = props;
	const apiSlice = useAppSelector((state) => state[apiEndpoints.reducerPath]);
	const resolver = useMemo(() => buildProposalNameResolver(apiSlice), [apiSlice]);
	const summary = useMemo(
		() => summarizeProposal(proposal.toolName, proposal.argumentsJson, resolver),
		[proposal.toolName, proposal.argumentsJson, resolver]
	);

	if (proposal.state === "OUTCOME_UNKNOWN" && proposal.priceImport)
		return <OutcomeUnknownCard card={proposal.priceImport} title={summary.title} />;
	if (proposal.state === "COMMITTING") return <CommittingCard proposal={proposal} title={summary.title} />;
	if (proposal.state === "CONFIRMED" && proposal.priceImport)
		return <PriceImportResultCard proposal={proposal} title={summary.title} />;
	if (proposal.state === "CONFIRMED")
		return (
			<ConfirmedCard resultSummary={readableResultText(proposal.resultSummary) ?? "Done."} title={summary.title} />
		);
	if (proposal.state === "FAILED")
		return (
			<FailedCard
				resultsLink={proposal.priceImport?.reviewLink}
				resultSummary={readableResultText(proposal.resultSummary) ?? "The action failed."}
				title={summary.title}
			/>
		);
	if (proposal.state === "CANCELLED")
		return (
			<CancelledCard
				text={
					proposal.priceImport && proposal.resultSummary && proposal.resultSummary !== proposal.summary
						? (readableResultText(proposal.resultSummary) ?? "Cancelled")
						: "Cancelled"
				}
				title={summary.title}
			/>
		);
	return <PendingCard {...props} summary={summary} />;
};

export default FernieConfirmCard;
